import axios from 'axios';
import { SORT_DIRECTION_DTO } from '../dtos/query-params.dto';
import { exportExcelMapper } from '../mappers/export-excel.mapper';
import { noteMapper } from '../mappers/note.mapper';
import { orderItemMapper } from '../mappers/order-item.mapper';
import { orderNoteMapper } from '../mappers/order-note.mapper';
import { orderPaymentCardMapper } from '../mappers/order-payment-card.mapper';
import { orderPaymentCheckMapper } from '../mappers/order-payment-check.mapper';
import { orderSummaryMapper } from '../mappers/order-summary.mapper';
import { orderMapper } from '../mappers/order.mapper';
import { paginationInfoMapper } from '../mappers/pagination-info.mapper';
import { paymentItemMapper } from '../mappers/payment-item.mapper';
import { queryParamsMapper } from '../mappers/query-params.mapper';
import { userMapper } from '../mappers/user.mapper';
import { ExportExcel } from '../models/export-excel';
import { NoteCreation } from '../models/note';
import {
  Order,
  OrderCreation, OrderDetail, OrderNonAdminCreation,
  OrderUpdate,
  OrderWithCredentials,
} from '../models/order';
import { OrderBundle } from '../models/order-bundle';
import { OrderItem, OrderItemUpdate } from '../models/order-item';
import { OrderNote } from '../models/order-note';
import { OrderPaymentCardCreation } from '../models/order-payment-card';
import { OrderPaymentCheckCreation } from '../models/order-payment-check';
import { PaginationInfo } from '../models/pagination-info';
import { PaymentItem } from '../models/payment-item';
import { QueryParams } from '../models/query-params';
import { User } from '../models/user';
import { appUrlsConfig } from './app-urls.config';
import { axiosService, catchHttpError } from './axios.service';
import { PayPalCreateOrderDto } from '../dtos/paypal-create-order.dto';

/**
 * Get orders.
 * @param {object} options Query params options.
 * @param {QueryParams} options.options Query params options.
 * @param {User} options.learner Learner.
 * @param {User} options.groupAdmin Group admin.
 * @param {boolean} options.isStudent Whether user is student.
 * @param {boolean} options.isPfsAdmin Whether user is pfsAdmin.
 * @returns {Promise<PaginationInfo>}
 */
async function getOrders({
  options, isStudent, learner, groupAdmin, isPfsAdmin,
}) {
  const optionsDto = queryParamsMapper.toDto(options);
  const {
    sortBy, sortDirection, orderId, learnerName, classCategoryIds, isPaid, allowAssign,
  } = options;

  const params = {
    ...optionsDto,
    owner_id: learner?.id ?? groupAdmin?.id,
    is_pfs_admin: isPfsAdmin? 1: 0,
    order_by: sortBy || 'create_date',
    sort_order: sortDirection || SORT_DIRECTION_DTO.DESC,
  };

  if (orderId) {
    params.order_id = orderId;
  }

  if (learnerName) {
    params.learner_name = learnerName;
  }

  if (classCategoryIds?.length) {
    params.class_category_ids = classCategoryIds;
  }

  if (isPaid != null) {
    params.is_paid = isPaid ? 1 : 0;
  }

  if (allowAssign != null) {
    params.allow_assign = allowAssign ? 1 : 0;
  }

  const response = await axiosService.get(isStudent ? appUrlsConfig.orders.getOrders : appUrlsConfig.orders.admin.getOrders, { params });
  return paginationInfoMapper.fromDto(response.data, orderMapper.fromDto);
}

/**
 * Get orders notes.
 * @param {Order["id"]} id Order ID.
 * @returns {Promise<OrderNote[]>}
 */
async function getNotes(id) {
  return catchHttpError(async () => {
    const result = await axiosService.get(appUrlsConfig.orders.admin.getNotes(id));
    return result.data.map(note => orderNoteMapper.fromDto(note));
  });
}

/**
 * Add order note.
 * @param {object} data Note creation data.
 * @param {number} data.id Order ID.
 * @param {NoteCreation} data.data Note creation data.
 * @returns {Promise<OrderNote>}
 */
async function addNote({ id, data }) {
  return catchHttpError(async () => {
    const noteDto = noteMapper.toCreateDto(data);
    const result = await axios.post(appUrlsConfig.orders.admin.addNote(id), noteDto);
    return orderNoteMapper.fromDto(result.data);
  });
}

/**
 * Export orders.
 * @param {object} data Query params.
 * @param {QueryParams} data.queryParams Query params.
 * @param {User} data.groupAdmin Group admin.
 * @param {User} data.learner Group admin.
 * @param {boolean} data.isPfsAdmin Current user isPfsAdmin.
 * @param {Array} data.ids Array of order IDs.
 * @returns {Promise<ExportExcel>}.
 */
async function exportOrders({
  queryParams, groupAdmin, learner, isPfsAdmin, ids,
}) {
  const response = await axiosService.get(appUrlsConfig.orders.admin.exportOrders, {
    params: {
      ...queryParamsMapper.toDto(queryParams),
      owner_id: learner?.id ?? groupAdmin?.id,
      is_pfs_admin: isPfsAdmin ? 1 : 0,
      order_by: 'create_date',
      sort_order: SORT_DIRECTION_DTO.DESC,
      ids,
    },
  });

  return exportExcelMapper.fromDto(response.data);
}

/**
 * Search order's page in pagination.
 * @param {object} data Data.
 * @param {number} data.orderId Order id.
 * @param {number} data.userId User id.
 * @param {boolean} data.isPfsAdmin Current User isPfsAdmin.
 * @param {number} data.perPage Results per page.
 * @param {string} data.sortBy Sort by field.
 * @param {string} data.sortOrder Sort order.
 * @returns {Promise<object>}
 */
async function searchOrder({ orderId, userId, isPfsAdmin, perPage, sortBy, sortOrder }) {
  const params = {
    paging_by_order_id: orderId,
    owner_id: userId,
    is_pfs_admin: isPfsAdmin ? 1 : 0,
    order_by: sortBy ?? 'create_date',
    sort_order: sortOrder ?? SORT_DIRECTION_DTO.DESC,
    per_page: perPage,
  };
  const response = await axiosService.get(appUrlsConfig.orders.admin.searchOrders, { params });
  return response.data;
}

/**
 * Update order.
 * @param {object} root0 Order.
 * @param {number} root0.orderId Order ID.
 * @param {OrderUpdate} root0.params Order update params.
 */
async function updateOrder({ orderId, params }) {
  const orderUpdateDto = orderMapper.toOrderUpdateDto(params);
  await axiosService.put(appUrlsConfig.orders.admin.updateOrder(orderId), orderUpdateDto);
}

/**
 * Allow order to be assigned without payment (or pay later).
 * @param {object} root0 Order.
 * @param {number} root0.orderId Order ID.
 * @param {OrderUpdate} root0.params Order assign params.
 * @returns {Promise<PaymentItem>}
 */
async function payLater({ orderId, params }) {
  const orderUpdateDto = orderMapper.toOrderUpdateDto(params);
  const result = await axiosService.post(appUrlsConfig.orders.admin.payLater(orderId), orderUpdateDto);
  return paymentItemMapper.fromDto(result.data);
}

/**
 * Get order detail.
 * @param {number} orderId Order id.
 * @returns {Promise<OrderDetail>}
 */
async function getOrderDetail(orderId) {
  const response = await axiosService.get(appUrlsConfig.orders.admin.getOrderDetail(orderId), {
    params: {
      include: 'refunds',
    },
  });
  return orderMapper.detailFromDto(response.data);
}

/**
 * Get order bundle.
 * @param {number} orderId Order id.
 * @returns {Promise<OrderBundle[]>}
 */
async function getOrderBundle(orderId) {
  const response = await axiosService.get(appUrlsConfig.orders.admin.getOrderBundle(orderId));
  return response.data.map(dto => orderMapper.orderBundleFromDto(dto));
}

/**
 * Update order item inside an order.
 * @param {Order["id"]} orderId Order ID.
 * @param {OrderItem["id"]} orderItemId Order item ID.
 * @param {OrderItemUpdate} data Update data.
 * @returns {Promise<Order>}
 */
async function updateOrderItem(orderId, orderItemId, data) {
  return catchHttpError(async () => {
    const dataDto = orderItemMapper.toUpdateDto(data);
    const response = await axiosService.put(appUrlsConfig.orders.admin.updateOrderItem(orderId, orderItemId), dataDto);
    return orderMapper.fromDto(response.data);
  });
}

/**
 * Remove order item inside an order.
 * @param {Order["id"]} orderId Order ID.
 * @param {OrderItem["id"]} orderItemId Order item ID.
 * @returns {Promise<void>}
 */
async function removeOrderItem(orderId, orderItemId) {
  return catchHttpError(async () => {
    await axiosService.delete(appUrlsConfig.orders.admin.removeOrderItem(orderId, orderItemId));
  });
}

/**
 * Remove order.
 * @param {Order["id"]} orderId Order ID.
 * @returns {Promise<void>}
 */
async function removeOrder(orderId) {
  return catchHttpError(async () => {
    await axiosService.delete(appUrlsConfig.orders.admin.removeOrder(orderId));
  });
}

/**
 * Create order.
 * @param {OrderCreation} order Order ID.
 * @returns {Promise<Order>}
 */
async function createOrder(order) {
  return catchHttpError(async () => {
    const dataDto = orderMapper.toCreationDto(order);
    const response = await axiosService.post(appUrlsConfig.orders.admin.createOrder, dataDto);
    return orderMapper.fromDto(response.data);
  });
}

/**
 * Create PayPal order.
 * @param {PayPalCreateOrderDto} data Order ID.
 * @returns {Promise<object>}
 */
async function createPayPalOrder(data) {
  return catchHttpError(async () => {
    const response = await axiosService.post(appUrlsConfig.paypal.create, data);
    return response.data;
  });
}

/**
 * Create order.
 * @param {OrderNonAdminCreation} order Order ID.
 * @returns {Promise<Order>}
 */
async function createNonAdminOrder(order) {
  return catchHttpError(async () => {
    const dataDto = orderMapper.toNonAdminCreationDto(order);
    const response = await axiosService.post(appUrlsConfig.orders.createOrder, dataDto);
    return orderMapper.fromDto(response.data);
  });
}

/**
 * Pay order by card.
 * @param {object} data Post data.
 * @param {OrderPaymentCardCreation} data.cardInfo Card information.
 * @param {Order["id"]} data.orderId Order ID.
 * @returns {Promise<PaymentItem>}
 */
async function payByCard({ cardInfo, orderId }) {
  return catchHttpError(async () => {
    const dto = orderPaymentCardMapper.toCreationDto(cardInfo);
    const result = await axiosService.post(appUrlsConfig.orders.admin.payByCard(orderId), dto);
    return paymentItemMapper.fromDto(result.data);
  });
}

/**
 * Pay order by PayPal.
 * @param {object} data Post data.
 * @param {Order["id"]} data.orderId Order ID.
 * @param {object} data.capturePayPalData Capture PayPal data.
 * @returns {Promise<PaymentItem>}
 */
async function payByPayPal({ orderId, capturePayPalData }) {
  return catchHttpError(async () => {
    const result = await axiosService.post(appUrlsConfig.orders.admin.payByPayPal(orderId), capturePayPalData);
    return paymentItemMapper.fromDto(result.data);
  });
}

/**
 * Pay order by check.
 * @param {object} data Post data.
 * @param {OrderPaymentCheckCreation} data.checkInfo Check information.
 * @param {Order["id"]} data.orderId Order ID.
 * @returns {Promise<PaymentItem>}
 */
async function payByCheck({ checkInfo, orderId }) {
  return catchHttpError(async () => {
    const dto = orderPaymentCheckMapper.toCreationDto(checkInfo);
    const result = await axiosService.post(appUrlsConfig.orders.admin.payByCheck(orderId), dto);
    return paymentItemMapper.fromDto(result.data);
  });
}

/**
 * Pay multiple orders by card.
 * @param {object} data Payment data.
 * @returns {Promise<PaymentItem>}
 */
async function payMultipleOrdersByCard(data) {
  return catchHttpError(async () => {
    const response = await axiosService.post(appUrlsConfig.orders.admin.payMultipleOrdersByCard, data);
    return paymentItemMapper.fromDto(response.data);
  });
}

/**
 * Pay multiple orders by Apple Pay.
 * @param {object} data Payment data.
 * @returns {Promise<PaymentItem>}
 */
async function payMultipleOrdersByApplePay(data) {
  return catchHttpError(async () => {
    const response = await axiosService.post(appUrlsConfig.orders.admin.payMultipleOrdersByApplePay, data);
    return paymentItemMapper.fromDto(response.data);
  });
}

/**
 * Pay multiple orders by PayPal.
 * @param {object} data Payment data.
 * @returns {Promise<PaymentItem>}
 */
async function payMultipleOrdersByPayPal(data) {
  return catchHttpError(async () => {
    const response = await axiosService.post(appUrlsConfig.orders.admin.payMultipleOrdersByPayPal, data);
    return paymentItemMapper.fromDto(response.data);
  });
}

/**
 * Pay for free order.
 * @param {object} data Post data.
 * @param {Order["id"]} data.orderId Order ID.
 * @returns {Promise<PaymentItem>}
 */
async function payForFreeOrder({ orderId }) {
  return catchHttpError(async () => {
    // Amount 0 represent for free order, and we need to send it this way to BE.
    const dto = orderPaymentCheckMapper.toCreationDto({ amount: 0 });
    const result = await axiosService.post(appUrlsConfig.orders.admin.payForFreeOrder(orderId), dto);
    return paymentItemMapper.fromDto(result.data);
  });
}

/**
 * Pay order by Apple Pay.
 * Use when Group Admin user purchase more packages (admin impersonate or group admin logged in).
 * @param {object} data Post data.
 * @param {Order["id"]} data.orderId Order ID.
 * @param {object} data.applePayData Apple Pay data.
 * @param {boolean} data.sendEmail Send email when group admin purchase
 * @returns {Promise<PaymentItem>}
 */
async function payByApplePay({ orderId, applePayData, sendEmail = false }) {
  return catchHttpError(async () => {
    const { paymentData, billingContact } = applePayData;
    const customer = billingContact ? {
      firstName: billingContact.givenName,
      lastName: billingContact.familyName,
      address: billingContact.addressLines ? billingContact.addressLines[0] : null,
      city: billingContact.locality,
      state: billingContact.administrativeArea,
      zip: billingContact.postalCode,
      country: billingContact.countryCode,
      email: billingContact.emailAddress,
    } : {};

    const payload = {
      orderId,
      paymentData,
      customer,
      sendEmail,
    };
    const result = await axiosService.post(appUrlsConfig.applePay.process, payload);
    if (result.data.success && result.data.payment) {
      return paymentItemMapper.fromDto(result.data.payment);
    }
    throw new Error(result.data.error || 'Payment failed with Apple Pay');
  });
}

/**
 * Mark order as paid.
 * @param {object} data Post data.
 * @param {Order["id"]} data.orderId Order ID.
 * @returns {Promise<void>}
 */
async function markAsPaid({ orderId }) {
  return catchHttpError(async () => {
    await axiosService.post(appUrlsConfig.orders.admin.markPaid(orderId));
  });
}

/**
 * Checkout order.
 * @param {object} data Data.
 * @returns {Promise<Order>}
 */
async function checkoutOrderByCard(data) {
  return catchHttpError(async () => {
    const response = await axiosService.post(appUrlsConfig.orders.checkoutOrderByCardOrApplePay, {
      ...orderSummaryMapper.toDto(data),
      ...orderPaymentCardMapper.toCreationDto(data),
      user_id: data.user.id,
    });
    return orderMapper.fromDto(response.data);
  });
}

/**
 * Checkout order by Apple Pay (for logged-in Group Admin users purchasing packages).
 * Currently have issue Business Name always empty
 * @param {any} data Checkout data including packages, user, and applePayData.
 * @returns {Promise<Order>}
 */
async function checkoutOrderByApplePay(data) {
  return catchHttpError(async () => {
    const response = await axiosService.post(appUrlsConfig.orders.checkoutOrderByCardOrApplePay, {
      ...orderSummaryMapper.toDto(data),
      user_id: data.user.id,
      applePayData: data.applePayData,
    });
    return orderMapper.fromDto(response.data);
  });
}

/**
 * Checkout order by PayPal.
 * @param {object} data Post data.
 * @returns {Promise<Order>}
 */
async function checkoutOrderByPayPal(data) {
  return catchHttpError(async () => {
    const response = await axiosService.post(appUrlsConfig.orders.checkoutOrderByCardOrApplePay, {
      ...orderSummaryMapper.toDto(data),
      user_id: data.user.id,
      paypal_order_id: data.paypal_order_id,
    });
    return orderMapper.fromDto(response.data);
  });
}

/**
 * Checkout order.
 * @param {object} data Data.
 * @returns {Promise<OrderWithCredentials>}
 */
async function checkout(data) {
  return catchHttpError(async () => {
    const response = await axiosService.post(appUrlsConfig.checkout.checkout, {
      ...orderSummaryMapper.toDto(data),
      ...orderPaymentCardMapper.toCreationDto(data),
      user: userMapper.toCreationExtendDto(data.user),
      g_recaptcha_response: data.googleRecaptcha,
    });
    return orderMapper.fromOrderWithCredentialDto(response.data, {
      username: response.headers.username,
      password: response.headers.password,
    });
  });
}

/**
 * Validate checkout payload before placing an order.
 * @param {object} data Data.
 * @returns {Promise<object>}
 */
async function validateCheckout(data) {
  return catchHttpError(async () => {
    const response = await axiosService.post(appUrlsConfig.checkout.validateCheckout, {
      ...orderSummaryMapper.toDto(data),
      ...orderPaymentCardMapper.toCreationDto(data),
      user: userMapper.toCreationExtendDto(data.user),
      g_recaptcha_response: data.googleRecaptcha,
      applePayData: data.applePayData || null,
      paypal_order_id: data.paypal_order_id || null,
    });
    return response.data;
  });
}

/**
 * Checkout (guest) with Apple Pay (for new users without account).
 * Same as checkout but uses Apple Pay instead of credit card.
 * @param {any} data Checkout data including packages, user info, and applePayData.
 * @returns {Promise<OrderWithCredentials>}
 */
async function checkoutByApplePay(data) {
  return catchHttpError(async () => {
    const response = await axiosService.post(appUrlsConfig.checkout.checkout, {
      ...orderSummaryMapper.toDto(data),
      user: userMapper.toCreationExtendDto(data.user),
      g_recaptcha_response: data.googleRecaptcha,
      applePayData: data.applePayData,
    });
    return orderMapper.fromOrderWithCredentialDto(response.data, {
      username: response.headers.username,
      password: response.headers.password,
    });
  });
}

/**
 * Checkout (guest) with PayPal (for new users without account).
 * @param {any} data Checkout data including packages, user info, and paypal_order_id.
 * @returns {Promise<OrderWithCredentials>}
 */
async function checkoutByPayPal(data) {
  return catchHttpError(async () => {
    const response = await axiosService.post(appUrlsConfig.checkout.checkout, {
      ...orderSummaryMapper.toDto(data),
      user: userMapper.toCreationExtendDto(data.user),
      g_recaptcha_response: data.googleRecaptcha,
      paypal_order_id: data.paypal_order_id,
    });

    return orderMapper.fromOrderWithCredentialDto(response.data, {
      username: response.headers.username,
      password: response.headers.password,
    });
  });
}

/**
 * Create order.
 * @param {object} data Data.
 * @returns {Promise<Order>}
 */
async function createPublicOrder(data) {
  return catchHttpError(async () => {
    const response = await axiosService.post(appUrlsConfig.orders.createOrder, {
      ...orderSummaryMapper.toDto(data),
      user_id: data.user.id,
    });
    return orderMapper.fromDto(response.data);
  });
}

/**
 * Check if user is a Pandargca corporate admin.
 * @param {number} userId User ID.
 * @returns {Promise<{isPandargca: boolean}>}
 */
async function checkIsPandargca(userId) {
  const response = await axiosService.get(appUrlsConfig.orders.admin.isUserPandargca, {
    params: { user_id: userId },
  });
  return response.data;
}

/**
 * Generate invoice for pandargca corporate code registrations.
 * @param {number} userId User ID.
 * @param {number[]} orderIds Order IDs.
 * @returns {Promise<{name: string, url: string}>}
 */
async function generatePandargcaInvoice(userId, orderIds = []) {
  return catchHttpError(async () => {
    const response = await axiosService.get(appUrlsConfig.orders.admin.genInvoicePandargca, {
      params: {
        user_id: userId,
        'order_ids[]': orderIds,
      },
    });
    return response.data;
  });
}

export const orderService = {
  getOrders,
  searchOrder,
  updateOrderItem,
  createOrder,
  createPayPalOrder,
  removeOrderItem,
  payByCard,
  payByCheck,
  payByPayPal,
  payByApplePay,
  payLater,
  updateOrder,
  createNonAdminOrder,
  checkoutOrderByCard,
  checkoutOrderByApplePay,
  checkoutOrderByPayPal,
  createPublicOrder,
  getOrderBundle,
  getOrderDetail,
  markAsPaid,
  exportOrders,
  payForFreeOrder,
  checkout,
  validateCheckout,
  checkoutByApplePay,
  checkoutByPayPal,
  removeOrder,
  getNotes,
  addNote,
  payMultipleOrdersByCard,
  payMultipleOrdersByApplePay,
  payMultipleOrdersByPayPal,
  checkIsPandargca,
  generatePandargcaInvoice,
};
